/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Box,
	Group,
	Title,
	Radio as NativeRadio,
} from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './AddUserForm.module.css';

// Utils
import {
	cleanObject,
	convertArrayToLabelValue,
} from '@utils/functions';

// Icons
import useStore from '@store/index';

// I18n
import { useTranslation } from 'react-i18next';
import { TextInputV2 } from '@components/atoms';
import { useMediaQuery } from '@mantine/hooks';
import { UseFormReturnType } from '@mantine/form';
import { TFormValuesUser } from '@features/resources/users/types';
import { useAddUser } from '@features/resources/users/api/create-user';
import { useAllOrganizations } from 'shared/api/get-all-organizations';
import { ROLES_OPTIONS } from '@utils/options';
import { ProfilePictureUpload } from '@components/molecules/ProfilePictureUpload';
import { useUploadUserPrivatePicture } from '@api/upload-user-private-picture';
import { useState } from 'react';
import { useRemovePrivateMedia } from '@api/remove-private-picture';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddUserForm = (props: {
	form: UseFormReturnType<TFormValuesUser>;
	onCancel: () => void;
	onBack: () => void;
	open: boolean;
}) => {
	const [pictureData, setPictureData] =
		useState<null | FormData>(null);

	const {
		usersPage,
		usersPageSize,
		setUserOrganization,
	} = useStore();

	const { t } = useTranslation();

	const { data: organizations } = useAllOrganizations();

	const removePictureMutation = useRemovePrivateMedia();

	const addUser = useAddUser({
		page: usersPage,
		size: usersPageSize,
		mutationConfig: {
			onSuccess() {
				props.onBack();
			},
			onError(error, variables, context) {
				if (variables.avatar) {
					removePictureMutation.mutate(variables.avatar);
				}
			},
		},
	});

	const uploadPictureMutation =
		useUploadUserPrivatePicture({
			mutationConfig: {
				onSuccess(data, variables, context) {
					props.form.validate();
					if (props.form.isValid()) {
						const {
							language,
							organization,
							avatar,
							...restFormValues
						} = props.form.values;

						addUser.mutate(
							cleanObject({
								...restFormValues,
								...(organization &&
								restFormValues.role === 'USER'
									? { organization }
									: {}),
								locale: {
									language,
								},
								avatar: data.id,
							})
						);
					}
				},
			},
		});

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			if (
				props.form.getValues().avatar !== null &&
				props.form.isValid()
			) {
				uploadPictureMutation.mutate(pictureData);
			} else {
				props.form.validate();
				if (props.form.isValid()) {
					const {
						language,
						organization,
						...restFormValues
					} = props.form.values;

					addUser.mutate(
						cleanObject({
							...restFormValues,
							...(organization &&
							restFormValues.role === 'USER'
								? { organization }
								: {}),
							locale: {
								language,
							},
						})
					);
				}
			}
		};
		const handleOnUploadProfile = (
			formData: FormData | string | null
		) => {
			if (typeof formData !== 'string') {
				props.form.setFieldValue('avatar', formData);
				setPictureData(formData);
			} else {
				props.form.setFieldValue('avatar', null);
			}
		};

		const {
			onChange: onChangeProject,
			...organizationProps
		} = props.form.getInputProps('organization');

		const handleChangeOrganization = (_id: string) => {
			props.form.setFieldValue('organization', _id);
			if (_id) setUserOrganization(_id);
		};

		/* ******************************** Media Query ******************************* */
		const isBigScreen = useMediaQuery(
			'(min-width: 120em)'
		);

		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(() => {})}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{t('global.users')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('users.addUser')}
					</Text>
				</Box>
				{/* Row for Role */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							notruncate
							visibleFrom="xxl"
							size="md"
							label={t('global.role')}
							withAsterisk
							data={ROLES_OPTIONS}
							placeholder={t('global.role')}
							{...props.form.getInputProps('role')}
						/>
						<Select
							notruncate
							hiddenFrom="xxl"
							size="sm"
							label={t('global.role')}
							withAsterisk
							data={ROLES_OPTIONS}
							placeholder={t('global.role')}
							{...props.form.getInputProps('role')}
						/>
					</Box>
				</Box>

				{/* Row for Organization */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								props.form.getValues().role !== 'USER'
							}
							visibleFrom="xxl"
							size="md"
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								organizations
							)}
							placeholder={t('global.organization')}
							onChange={handleChangeOrganization}
							{...organizationProps}
						/>
						<Select
							disabled={
								props.form.getValues().role !== 'USER'
							}
							hiddenFrom="xxl"
							size="sm"
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								organizations
							)}
							placeholder={t('global.organization')}
							onChange={handleChangeOrganization}
							{...organizationProps}
						/>
					</Box>
				</Box>

				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInputV2
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.firstName')}
							placeholder={t('global.firstName')}
							{...props.form.getInputProps('firstName')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<TextInputV2
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.lastName')}
							placeholder={t('global.lastName')}
							{...props.form.getInputProps('lastName')}
						/>
					</Box>
				</Box>
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInputV2
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.email')}
							placeholder={t('global.email')}
							{...props.form.getInputProps('email')}
						/>
					</Box>
				</Box>
				<Box className={classes.row}>
					<Box
						style={{
							width: '30%',
						}}
					>
						<NativeRadio.Group
							withAsterisk
							label={t('global.language')}
							{...props.form.getInputProps('language')}
						>
							<Group gap={'25px'} pt={'md'}>
								<NativeRadio
									variant="outline"
									size={isBigScreen ? 'sm' : 'xs'}
									label={'Fr'}
									value={'fr'}
								/>
								<NativeRadio
									variant="outline"
									size={isBigScreen ? 'sm' : 'xs'}
									label={'En'}
									value={'en'}
								/>
							</Group>
						</NativeRadio.Group>
					</Box>
					<Box className={classes.containerInput}>
						<TextInputV2
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.phoneNumber')}
							placeholder={t('global.phoneNumber')}
							{...props.form.getInputProps('phoneNumber')}
						/>
					</Box>
				</Box>

				{/* Row for Picture */}
				<ProfilePictureUpload
					onUpload={handleOnUploadProfile}
					src={''}
				/>

				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							hiddenFrom="xxl"
							size="md"
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							fullWidth
							size="lg"
							variant="transparent"
							visibleFrom="xxl"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={handleSubmitForm}
							loading={addUser.isLoading}
						>
							{t('users.addUser')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
